import type { MouseEventHandler } from "react";

import type { GitStatusResult } from "@cmmd-forge/contracts";
import type { ThreadStatusPill } from "../Sidebar.logic";

// ---------------------------------------------------------------------------
// SidebarThreadRow — pure helpers and local types
// ---------------------------------------------------------------------------

export type ThreadPr = GitStatusResult["pr"];

export interface TerminalStatusIndicator {
  label: "Terminal process running";
  colorClass: string;
  pulse: boolean;
}

export interface PrStatusIndicator {
  label: "PR open" | "PR closed" | "PR merged";
  colorClass: string;
  tooltip: string;
  url: string;
}

export interface ThreadLeadIndicator {
  kind: "pr-merged" | "working" | "failed" | "needs-attention" | "unread" | "idle";
  tooltip: string;
  colorClass: string;
  dotClass?: string;
  pulse?: boolean;
}

export function terminalStatusFromRunningIds(
  runningTerminalIds: string[],
): TerminalStatusIndicator | null {
  if (runningTerminalIds.length === 0) return null;
  return {
    label: "Terminal process running",
    colorClass: "text-hue-cyan-foreground",
    pulse: true,
  };
}

export function prStatusIndicator(
  pr: ThreadPr,
  input: { hasThreadLinkedPr: boolean },
): PrStatusIndicator | null {
  if (!input.hasThreadLinkedPr) return null;
  if (!pr || pr.state !== "merged") return null;
  return {
    label: "PR merged",
    colorClass: "text-hue-sky-foreground",
    tooltip: `#${pr.number} PR merged: ${pr.title}`,
    url: pr.url,
  };
}

export function resolveThreadLeadIndicator(input: {
  threadStatus: ThreadStatusPill | null;
  prStatus: PrStatusIndicator | null;
}): ThreadLeadIndicator {
  const { threadStatus, prStatus } = input;

  if (prStatus?.label === "PR merged") {
    return {
      kind: "pr-merged",
      tooltip: prStatus.tooltip,
      colorClass: prStatus.colorClass,
    };
  }

  if (threadStatus?.label === "Working" || threadStatus?.label === "Connecting") {
    return {
      kind: "working",
      tooltip: threadStatus.label,
      colorClass: "text-muted-foreground/70",
    };
  }

  if (
    threadStatus?.label === "Failed" ||
    threadStatus?.label === "Conflicted" ||
    threadStatus?.label === "Stuck"
  ) {
    return {
      kind: "failed",
      tooltip: threadStatus.label,
      colorClass: "text-destructive-foreground",
      dotClass: "bg-destructive dark:bg-destructive/90",
      pulse: false,
    };
  }

  if (
    threadStatus?.label === "Pending Approval" ||
    threadStatus?.label === "Awaiting Input" ||
    threadStatus?.label === "Plan Ready" ||
    threadStatus?.label === "Needs Review" ||
    threadStatus?.label === "Interrupted"
  ) {
    return {
      kind: "needs-attention",
      tooltip: threadStatus.label,
      colorClass: "text-warning-foreground",
      dotClass: "bg-warning dark:bg-warning/90",
      pulse: false,
    };
  }

  if (threadStatus?.label === "Completed") {
    return {
      kind: "unread",
      tooltip: "Unread update",
      colorClass: "text-hue-sky-foreground",
      dotClass: "bg-hue-sky dark:bg-hue-sky/80",
      pulse: false,
    };
  }

  return {
    kind: "idle",
    tooltip: "Stopped",
    colorClass: "text-muted-foreground/55",
    dotClass: "bg-muted-foreground/50",
    pulse: false,
  };
}

export function shouldRenderThreadLeadIndicator(indicator: ThreadLeadIndicator): boolean {
  return indicator.kind !== "idle";
}

export function sidebarStatusDotMotionClass(input: { pulse?: boolean }): string {
  // An active dot takes the duty-cycled indicator animation the design system
  // mandates (DESIGN.md → "Looping indicators are duty-cycled"); Tailwind's
  // smooth `animate-pulse` is the thing that rule exists to keep out.
  //
  // The idle branch is deliberately NOT converted. It is a slower 3s breath
  // with no stepped equivalent in the token set, so swapping it to the 2s
  // `animate-status-pulse` would silently retime a distinct state rather than
  // port it. Adding a 3s duty-cycled token is a design-system decision, not a
  // drive-by in a card about the Activity thread row — tracked separately.
  return input.pulse
    ? "motion-safe:animate-status-pulse"
    : "motion-safe:animate-[pulse_3s_ease-in-out_infinite]";
}

/**
 * The branch a row should SHOW.
 *
 * `thread.branch` is written at provisioning — worktree create, the first-turn
 * branch rename, pipeline allocation — and never reconciled against git
 * afterwards. The only thing that corrects it is `useGitThreadBranchSync`, and
 * that is mounted by `GitActionsControl`, i.e. for the one thread you have
 * open. Every other row keeps its creation-time value for the life of the
 * thread, so a sidebar of threads nobody has opened reads as whatever branch
 * they were started from.
 *
 * The row already subscribes to live git status for its mismatch warning, so
 * the true answer is in hand and was simply not being displayed. Prefer it.
 *
 * **This diverges from t3code deliberately.** Upstream shows the recorded
 * branch and warns when the checkout has moved; that is coherent, but it means
 * the branch line answers "what was this started from" when the question being
 * asked of it is "where is this work now". Forge answers the second.
 *
 * The recorded branch remains the fallback, and it is the one that renders in
 * the common case: a sleeping cloud sprite reports no git status at all, so
 * `currentGitBranch` is null and the row shows what the thread recorded —
 * which for a fresh clone is the truth anyway.
 */
export function resolveDisplayedThreadBranch(input: {
  readonly threadBranch: string | null;
  readonly currentGitBranch: string | null;
}): string | null {
  const live = input.currentGitBranch?.trim();
  if (live !== undefined && live !== "") return live;
  const recorded = input.threadBranch?.trim();
  return recorded !== undefined && recorded !== "" ? recorded : null;
}

export function resolveThreadPr(
  threadBranch: string | null,
  gitStatus: GitStatusResult | null,
): ThreadPr | null {
  if (threadBranch === null || gitStatus === null || gitStatus.branch !== threadBranch) {
    return null;
  }
  return gitStatus.pr ?? null;
}

/**
 * The pin affordance is a button layered over the row's own click handling,
 * so a press must swallow the row activation (preventDefault plus
 * stopPropagation) and hand the row key to the parent's toggle.
 */
export function makePinClickHandler(
  onTogglePinned: ((threadKey: string) => void) | undefined,
  threadKey: string,
): MouseEventHandler<HTMLButtonElement> {
  return (event) => {
    event.preventDefault();
    event.stopPropagation();
    onTogglePinned?.(threadKey);
  };
}

/** The pin control's tooltip, for both directions of the pinned state. */
export function describePinAction(isPinned: boolean): string {
  return isPinned ? "Pinned" : "Pin thread";
}
